import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import BASE_URL from "../../config/config";
import "../../css/SingleArticle.css";
import CommentsSection from "./CommentsSection";

export default function SingleArticle() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [prevPost, setPrevPost] = useState(null);
  const [nextPost, setNextPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toc, setToc] = useState([]);
  const [commentsCount, setCommentsCount] = useState(0);
  const postBodyRef = useRef(null);

  // تابع برای دریافت تعداد کامنت‌ها
  const fetchCommentsCount = async (postId) => {
    try {
      const response = await fetch(
        `${BASE_URL}/posts/${postId}/comments/count`,
        {
          credentials: "include",
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.status === "success") {
          setCommentsCount(result.data.count);
        }
      }
    } catch (err) {
      console.error("خطا در دریافت تعداد نظرات:", err);
    }
  };

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/blog/posts/${slug}`, {
          credentials: "include",
        });
        if (!response.ok) throw new Error("پست یافت نشد");
        const result = await response.json();
        setPost(result.data);
        if (result.data?.category_id && result.data?.id) {
          const numericId = Number(result.data.id);
          fetchAdjacentPosts(result.data.category_id, numericId);
          fetchCommentsCount(numericId);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchAdjacentPosts = async (categoryId, currentPostId) => {
      try {
        setPrevPost(null);
        setNextPost(null);

        const response = await fetch(
          `${BASE_URL}/blog/posts?category_id=${categoryId}&status=published`,
          { credentials: "include" }
        );
        if (!response.ok) return;

        const result = await response.json();
        const posts = result.data || [];
        const currentIndex = posts.findIndex((p) => p.id === currentPostId);
        if (currentIndex > 0) setPrevPost(posts[currentIndex - 1]);
        if (currentIndex < posts.length - 1)
          setNextPost(posts[currentIndex + 1]);
      } catch (err) {
        console.error("خطا در دریافت پست‌های مجاور:", err);
        setPrevPost(null);
        setNextPost(null);
      }
    };

    fetchPost();
  }, [slug]);

  useEffect(() => {
    if (!post?.content || !postBodyRef.current) return;
    const parser = new DOMParser();
    const doc = parser.parseFromString(post.content, "text/html");
    const headings = doc.querySelectorAll("h2, h3");
    const tocItems = [];
    const processed = new Set();

    headings.forEach((heading) => {
      const text = heading.textContent.trim();
      if (!text) return;
      let id = text
        .toLowerCase()
        .replace(/[^a-z0-9آ-ی]+/g, "-")
        .replace(/^-+|-+$/g, "");
      let originalId = id;
      let counter = 1;
      while (processed.has(id)) {
        id = `${originalId}-${counter}`;
        counter++;
      }
      processed.add(id);
      heading.id = id;
      tocItems.push({
        id,
        text,
        level: heading.tagName.toLowerCase(),
      });
    });

    postBodyRef.current.innerHTML = doc.body.innerHTML;
    setToc(tocItems);
  }, [post?.content]);

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen">
        در حال بارگذاری...
      </div>
    );
  if (error) return <div className="text-red-500 text-center p-4">{error}</div>;
  if (!post)
    return <div className="text-gray-500 text-center p-4">پست یافت نشد</div>;

  // ✅ محاسبه زمان مطالعه با فرمت درست
  const formatReadTime = (time) => {
    const minutes = Math.max(time || 1, 1);
    return `${minutes} دقیقه`;
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* بخش هدر و همرو مقاله */}
      <section className="hero-gradient py-20 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-black mb-6">{post.title}</h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto mb-6">
            {post.excerpt || "این یک خلاصه از مقاله است."}
          </p>
          <div className="flex justify-center space-x-6 space-x-reverse text-sm opacity-90">
            {/* ✅ تغییر از read_time به reading_time */}
            <span>⏱ {formatReadTime(post.reading_time)} مطالعه</span>
            {/* <span>👁 {post.views?.toLocaleString("fa-IR") || "۰"} بازدید</span> */}
            <span>💬 {commentsCount.toLocaleString("fa-IR")} نظر</span>
          </div>
        </div>
      </section>

      {/* محتوای اصلی با فهرست مطالب */}
      <section className="py-20">
        <div className="container mx-auto px-4 grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* فهرست مطالب */}
          {toc.length > 0 && (
            <aside className="bg-white rounded-2xl shadow-lg p-6 h-fit lg:sticky md:top-28">
              <h3 className="text-xl font-bold mb-4 text-gray-800">
                📋 فهرست مطالب
              </h3>
              <ul className="space-y-2 text-gray-600 text-sm">
                {toc.map((item) => (
                  <li
                    key={item.id}
                    className="whitespace-nowrap overflow-hidden text-ellipsis"
                  >
                    <a
                      href={`#${item.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        const el = document.getElementById(item.id);
                        if (el) {
                          el.scrollIntoView({ behavior: "smooth" });
                          window.history.pushState(null, "", `#${item.id}`);
                        }
                      }}
                      className="hover:text-blue-600 transition-colors white-space"
                    >
                      {item.text}
                    </a>
                  </li>
                ))}
              </ul>
            </aside>
          )}

          {/* متن اصلی مقاله */}
          <div className="md:col-span-3 bg-white rounded-3xl shadow-lg p-10 md:p-16">
            {post.cover_image && (
              <img
                src={`/uploads/blog/posts/${post.cover_image}`}
                alt={post.title}
                className="w-full h-auto  rounded-2xl mb-12 shadow-lg"
              />
            )}
            <div
              ref={postBodyRef}
              className="p-article prose prose-lg max-w-none "
            />

            {/* تگ‌ها */}
            {post.tags && post.tags.length > 0 && (
              <div
                id="articleTags"
                className="flex flex-wrap gap-3 mt-12 border-t pt-6"
              >
                {post.tags.map((t, index) => (
                  <Link
                    key={t.id || t.name || index} // fallback ایمن
                    to={`/blog/tags/${t.slug || t.name}`}
                    className="tag"
                  >
                    #{t.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ناوبری قبلی و بعدی */}
      {(prevPost || nextPost) && (
        <section className="py-10 bg-grey shadow-[0_4px_20px_rgba(0,0,0,0.05)] rounded-xl">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">
              ادامه مطالعه
            </h2>
            <div className="flex flex-col md:flex-row justify-center items-center gap-6">
              {prevPost && (
                <Link
                  to={`/blog/posts/${prevPost.slug}`}
                  className="w-full md:w-1/2 group"
                >
                  <div
                    className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm 
              transition-all duration-300 transform hover:-translate-y-2 hover:shadow-lg 
              text-center h-32 flex flex-col justify-between"
                  >
                    {/* دکمه نمایان‌تر */}
                    <div className="flex justify-center items-center mb-2">
                      <span className="flex items-center gap-2 px-3 py-1.5 text-sm font-bold text-white bg-blue-600 rounded-full shadow-md group-hover:bg-blue-700 transition">
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        مقاله قبلی
                      </span>
                    </div>

                    {/* عنوان */}
                    <h3 className="text-base font-semibold text-gray-800 group-hover:text-blue-600 transition-colors line-clamp-1">
                      {prevPost.title}
                    </h3>
                    <span className="text-xs text-gray-500">
                      ⏱ {formatReadTime(prevPost.reading_time)}
                    </span>
                  </div>
                </Link>
              )}

              {nextPost && (
                <Link
                  to={`/blog/posts/${nextPost.slug}`}
                  className="w-full md:w-1/2 group"
                >
                  <div
                    className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm 
              transition-all duration-300 transform hover:-translate-y-2 hover:shadow-lg 
              text-center h-32 flex flex-col justify-between"
                  >
                    {/* دکمه نمایان‌تر */}
                    <div className="flex justify-center items-center mb-2">
                      <span className="flex items-center gap-2 px-3 py-1.5 text-sm font-bold text-white bg-purple-600 rounded-full shadow-md group-hover:bg-purple-700 transition">
                        مقاله بعدی
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    </div>

                    {/* عنوان */}
                    <h3 className="text-base font-semibold text-gray-800 group-hover:text-purple-600 transition-colors line-clamp-1">
                      {nextPost.title}
                    </h3>
                    <span className="text-xs text-gray-500">
                      ⏱ {formatReadTime(nextPost.reading_time)}
                    </span>
                  </div>
                </Link>
              )}
            </div>
          </div>
        </section>
      )}
      <section className="py-10">
        {/* نظرات */}
        <CommentsSection postId={post.id} contentType="post" />
      </section>
    </div>
  );
}
